import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Coins,
  CreditCard,
  FileText,
  Home,
  MessageCircle,
  ScrollText,
  Shield,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";

export interface WorkspaceNavChild {
  label: string;
  path: string;
  Icon?: LucideIcon;
}

export interface WorkspaceNavLink {
  label: string;
  description?: string;
  path: string;
  Icon: LucideIcon;
  matchPaths?: string[];
  children?: WorkspaceNavChild[];
  /** Small pill next to the label (e.g. "Beta"). */
  badge?: string;
  /** data-tour anchor for the first-run GuidedTour. */
  tourId?: string;
  /** Listed in the phone drawer only; on desktop the same door sits in the top bar. */
  mobileOnly?: boolean;
}

const reportsNavLink: WorkspaceNavLink = {
  label: "Reports",
  description: "Market and legal analysis",
  path: "/reports",
  Icon: FileText,
  matchPaths: ["/reports/:reportId", "/views/:viewId"],
};

export const renterNavLinks: WorkspaceNavLink[] = [
  {
    label: "Dashboard",
    description: "Journey overview",
    path: "/tenant/dashboard",
    Icon: Home,
    children: [
      {
        label: "New Lease",
        path: "/tenant/dashboard/new-lease",
        Icon: ScrollText,
      },
      {
        label: "Upload Ejari",
        path: "/tenant/dashboard/running-ejari",
        Icon: FileText,
      },
    ],
  },
  {
    label: "Properties",
    description: "Offers & documents",
    path: "/tenant/properties",
    Icon: Building2,
  },
  {
    label: "Screening",
    description: "Your verified rental profile",
    path: "/tenant/financials",
    Icon: ShieldCheck,
    matchPaths: ["/tenant/financials/report"],
  },
  {
    label: "Maintenance",
    description: "Open jobs in your unit",
    path: "/tenant/jobs",
    Icon: Wrench,
    matchPaths: ["/tenant/jobs/:jobId"],
  },
  {
    label: "Billing",
    description: "Payment method",
    path: "/tenant/billing",
    Icon: CreditCard,
  },
];

// Placeholders for future personas; defining them here keeps nav config centralized.
export const agentNavLinks: WorkspaceNavLink[] = [
  {
    label: "Dashboard",
    description: "Pipeline overview",
    path: "/agent/dashboard",
    Icon: Home,
  },
  {
    label: "Properties",
    description: "Inventory & offers",
    path: "/agent/properties",
    Icon: Building2,
    // An addendum covers properties, so its page sits under Properties (design v1).
    matchPaths: ["/agent/properties/:propertyId", "/agent/addenda/:id"],
  },
  {
    label: "Money",
    description: "Rent received, costs, standing per unit",
    path: "/agent/money",
    Icon: Coins,
  },
  reportsNavLink,
  {
    label: "People",
    description: "Landlords & renters",
    path: "/agent/people",
    Icon: Users,
  },
  {
    label: "Maintenance",
    description: "Jobs across your portfolio",
    path: "/agent/jobs",
    Icon: Wrench,
    matchPaths: ["/agent/jobs/:jobId"],
  },
  {
    label: "Billing",
    description: "Plan & credits",
    path: "/agent/billing",
    Icon: CreditCard,
    tourId: "workspace-nav-billing",
  },
];

export const agencyManagerNavLink: WorkspaceNavLink = {
  label: "Access",
  description: "Agent–landlord access",
  path: "/agent/manage-access",
  Icon: Shield,
  tourId: "workspace-nav-manage-access",
};

// Agency back-office: Hazel conversations + the stack control plane.
// Shown to agency managers only (pushed alongside agencyManagerNavLink);
// the /pm routes are additionally role-gated by ProtectedRoute + server-side.
export const pmConsoleNavLink: WorkspaceNavLink = {
  label: "Hazel Console",
  description: "Conversations & stack",
  path: "/pm",
  Icon: MessageCircle,
  tourId: "agent-nav-console",
};

export const managerNavLinks: WorkspaceNavLink[] = [
  {
    label: "Dashboard",
    description: "Managed properties",
    path: "/manager/home",
    Icon: Home,
  },
  {
    label: "Properties",
    description: "Inventory you manage",
    path: "/manager/properties",
    Icon: Building2,
    matchPaths: ["/manager/properties/:propertyId"],
  },
  {
    label: "Maintenance",
    description: "Tenant requests",
    path: "/manager/maintenance",
    Icon: Wrench,
  },
  {
    label: "People",
    description: "Landlords & tenants",
    path: "/manager/people",
    Icon: Users,
  },
];

export const landlordNavLinks: WorkspaceNavLink[] = [
  {
    label: "Dashboard",
    description: "Your day at a glance",
    path: "/landlord/home",
    Icon: Home,
  },
  {
    label: "Properties",
    description: "Inventory & offers",
    path: "/landlord/properties",
    Icon: Building2,
    // An addendum covers properties, so its page sits under Properties (design v1).
    matchPaths: ["/landlord/properties/:propertyId", "/landlord/addenda/:id"],
  },
  {
    label: "Money",
    description: "Rent received, costs, standing",
    path: "/landlord/money",
    Icon: Coins,
  },
  reportsNavLink,
  {
    label: "People",
    description: "Renters & agents",
    path: "/landlord/people",
    Icon: Users,
  },
  {
    label: "Maintenance",
    description: "Jobs on your properties",
    path: "/landlord/jobs",
    Icon: Wrench,
    matchPaths: ["/landlord/jobs/:jobId"],
  },
  {
    label: "Billing",
    description: "Plan & credits",
    path: "/landlord/billing",
    Icon: CreditCard,
  },
];
